import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useGoogleLogin, googleLogout } from '@react-oauth/google';
import { authAPI, settingsAPI, setLoggingIn } from '../services/api';

const AuthContext = createContext(null);

// WHITELIST - Seuls ces emails peuvent se connecter
const AUTHORIZED_EMAILS = [
  'remicabrit@gmail.com',
  'admin@beam-consulting.fr',
];

// Durée de validité de la session (24 heures)
const SESSION_DURATION_HOURS = 24;

// Inactivité avant déconnexion (valeur par défaut, en minutes)
const DEFAULT_INACTIVITY_MINUTES = 30;

// Multi-session : stockage auth par onglet (sessionStorage) pour permettre plusieurs comptes dans plusieurs onglets
const AUTH_STORAGE = typeof window !== 'undefined' ? sessionStorage : null;

const loadInactivityTimeout = () => {
  try {
    const stored = localStorage.getItem('sessionInactivityMinutes');
    if (!stored) return DEFAULT_INACTIVITY_MINUTES;
    const minutes = parseInt(stored, 10);
    if (Number.isNaN(minutes) || minutes <= 0) {
      return DEFAULT_INACTIVITY_MINUTES;
    }
    return minutes;
  } catch {
    return DEFAULT_INACTIVITY_MINUTES;
  }
};

const isEmailAuthorized = (email) => {
  if (!email) return false;
  const emailLower = email.toLowerCase();
  return AUTHORIZED_EMAILS.some(authorized => authorized.toLowerCase() === emailLower);
};

const saveSession = (userInfo) => {
  const sessionData = {
    email: userInfo.email,
    name: userInfo.name,
    picture: userInfo.picture,
    expiresAt: new Date(Date.now() + SESSION_DURATION_HOURS * 60 * 60 * 1000).toISOString(),
    // Inclure les champs supplémentaires si présents
    ...(userInfo.user_id && { user_id: userInfo.user_id }),
    ...(userInfo.organization_id !== undefined && { organization_id: userInfo.organization_id }),
    ...(userInfo.role && { role: userInfo.role }),
    ...(userInfo.is_owner !== undefined && { is_owner: userInfo.is_owner }),
    ...(userInfo.is_superadmin !== undefined && { is_superadmin: userInfo.is_superadmin }),
    ...(userInfo.allowed_tabs !== undefined && { allowed_tabs: userInfo.allowed_tabs }),
    ...(userInfo.permissions !== undefined && { permissions: userInfo.permissions }),
  };
  if (AUTH_STORAGE) AUTH_STORAGE.setItem('googleSession', JSON.stringify(sessionData));
  return sessionData;
};

const loadSession = () => {
  if (!AUTH_STORAGE) return null;
  try {
    let sessionStr = AUTH_STORAGE.getItem('googleSession');
    // Migration unique : si sessionStorage vide mais localStorage a une session valide, copier (évite déconnexion au 1er chargement après déploiement multi-session)
    if (!sessionStr && typeof localStorage !== 'undefined') {
      const fromLocal = localStorage.getItem('googleSession');
      if (fromLocal) {
        try {
          const data = JSON.parse(fromLocal);
          const expiresAt = new Date(data.expiresAt);
          if (new Date() <= expiresAt && isEmailAuthorized(data.email)) {
            AUTH_STORAGE.setItem('googleSession', fromLocal);
            const userStr = localStorage.getItem('googleUser');
            if (userStr) AUTH_STORAGE.setItem('googleUser', userStr);
            const profileStr = localStorage.getItem('googleProfile');
            if (profileStr) AUTH_STORAGE.setItem('googleProfile', profileStr);
            sessionStr = fromLocal;
          }
        } catch (_) { /* ignorer */ }
      }
    }
    if (!sessionStr) return null;
    
    const sessionData = JSON.parse(sessionStr);
    
    const expiresAt = new Date(sessionData.expiresAt);
    if (new Date() > expiresAt) {
      AUTH_STORAGE.removeItem('googleSession');
      return null;
    }
    
    if (!isEmailAuthorized(sessionData.email)) {
      AUTH_STORAGE.removeItem('googleSession');
      return null;
    }
    
    return sessionData;
  } catch (error) {
    console.error('Error loading session:', error);
    AUTH_STORAGE.removeItem('googleSession');
    return null;
  }
};

const deleteSession = () => {
  if (AUTH_STORAGE) {
    AUTH_STORAGE.removeItem('googleSession');
    AUTH_STORAGE.removeItem('googleUser');
    AUTH_STORAGE.removeItem('googleProfile');
    AUTH_STORAGE.removeItem('app_jwt'); // JWT applicatif (multi-session backend)
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Charge l'avatar en base et met à jour le profil (picture)
const loadAvatarForSession = (session, setProfile, saveSession) => {
  const userId = session?.user_id || session?.id;
  if (!userId) return;
  settingsAPI.get('user_avatar', 1, userId)
    .then(avatarResponse => {
      if (avatarResponse.data?.param_value) {
        const userAvatar = avatarResponse.data.param_value;
        setProfile(prev => {
          if (!prev) return prev;
          const updatedProfile = { ...prev, picture: userAvatar };
          saveSession(updatedProfile);
          return updatedProfile;
        });
      }
    })
    .catch(() => {});
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sessionCheckDone, setSessionCheckDone] = useState(false);
  const [appJwtReady, setAppJwtReady] = useState(false); // true une fois qu'on a stocké un JWT (getProfile ou validate OK) → Oliver attend ça avant d'appeler l'API
  const [authError, setAuthError] = useState(null);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(() => loadInactivityTimeout());

  // Au chargement : valider le token avant de considérer la session active. Bloquer les autres requêtes pendant la validation (évite race condition avatar/TWS qui reçoivent 401 avant validate).
  useEffect(() => {
    const savedSession = loadSession();
    if (!savedSession) {
      setSessionCheckDone(true);
      return;
    }

    const hasAppJwt = AUTH_STORAGE && AUTH_STORAGE.getItem('app_jwt');

    // Bloquer toutes les requêtes (sauf /auth/validate et /auth/profile) jusqu'à la fin de la validation
    if (typeof window !== 'undefined') {
      window.authIsValidating = true;
    }

    const done = () => {
      if (typeof window !== 'undefined') {
        window.authIsValidating = false;
      }
      setSessionCheckDone(true);
    };

    // Normaliser is_owner / is_superadmin
    if (savedSession.is_owner === undefined) savedSession.is_owner = false;
    if (savedSession.is_superadmin === undefined) savedSession.is_superadmin = false;
    saveSession(savedSession);

    if (hasAppJwt) {
      // Token présent : valider avant d'afficher le dashboard. Ne supprimer la session que sur 401.
      // Après validate() OK, on rafraîchit le JWT via getProfile pour qu'Oliver (et les autres appels) ait un token frais.
      authAPI.validate()
        .then(() => {
          return authAPI.getProfile(savedSession.email).catch((e) => ({ __profileFailed: true, error: e }));
        })
        .then((res) => {
          if (res?.__profileFailed) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[Auth] getProfile après validate a échoué, utilisation du token existant');
            }
          } else if (res?.data?.access_token && AUTH_STORAGE) {
            AUTH_STORAGE.setItem('app_jwt', res.data.access_token);
            if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('appJwtUpdated'));
          }
          window.authIsValidating = false;
          setAppJwtReady(true);
          setProfile(savedSession);
          setSessionCheckDone(true);
          loadAvatarForSession(savedSession, setProfile, saveSession);
        })
        .catch((err) => {
          const isUnauthorized = err.response?.status === 401;
          if (isUnauthorized) {
            console.warn('[Auth] validate 401 → tentative de rafraîchir le token via getProfile...');
            authAPI.getProfile(savedSession.email)
              .then((res) => {
                if (res.data?.access_token && AUTH_STORAGE) {
                  AUTH_STORAGE.setItem('app_jwt', res.data.access_token);
                  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('appJwtUpdated'));
                }
                window.authIsValidating = false;
                setAppJwtReady(true);
                setProfile(savedSession);
                setSessionCheckDone(true);
                loadAvatarForSession(savedSession, setProfile, saveSession);
                done();
              })
              .catch(() => {
                console.warn('[Auth] getProfile après 401 a échoué → déconnexion');
                deleteSession();
                setAppJwtReady(false);
                setUser(null);
                setProfile(null);
                done();
              });
          } else {
            window.authIsValidating = false;
            setProfile(savedSession);
            setSessionCheckDone(true);
            loadAvatarForSession(savedSession, setProfile, saveSession);
            done();
          }
        });
    } else {
      // Pas de JWT : récupérer un token via getProfile.
      if (process.env.NODE_ENV === 'development') {
      }
      authAPI.getProfile(savedSession.email)
        .then((res) => {
          if (res.data?.access_token && AUTH_STORAGE) {
            AUTH_STORAGE.setItem('app_jwt', res.data.access_token);
            if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('appJwtUpdated'));
          }
          if (process.env.NODE_ENV === 'development') {
          }
          window.authIsValidating = false;
          setAppJwtReady(true);
          setProfile(savedSession);
          setSessionCheckDone(true);
          loadAvatarForSession(savedSession, setProfile, saveSession);
        })
        .catch((err) => {
          deleteSession();
          setAppJwtReady(false);
          setUser(null);
          setProfile(null);
          done();
        });
    }
  }, []);

  // Vérifier et mettre à jour le profil si is_owner ou is_superadmin sont manquants
  // OU si l'utilisateur est remicabrit@gmail.com (pour forcer la récupération depuis le backend)
  const profileFetchedRef = useRef(new Set());
  
  useEffect(() => {
    if (!profile || !profile.email) return;
    
    // Éviter de récupérer plusieurs fois pour le même email
    // SAUF pour remicabrit@gmail.com (forcer la récupération à chaque fois)
    const emailKey = profile.email.toLowerCase();
    const isRemicabrit = emailKey === 'remicabrit@gmail.com';
    
    if (!isRemicabrit && profileFetchedRef.current.has(emailKey)) {
      return;
    }
    
    const shouldFetchProfile = 
      !profile ||
      !profile?.email ||
      profile?.is_owner === undefined || 
      profile?.is_superadmin === undefined ||
      profile?.allowed_tabs === undefined ||
      isRemicabrit;
    
    if (shouldFetchProfile) {
      // Si profile est null, on ne peut pas récupérer le profil (pas d'email)
      if (!profile || !profile.email) {
        return;
      }
      
      if (!isRemicabrit) {
        profileFetchedRef.current.add(emailKey);
      }
      
      // Récupérer le profil depuis le backend
      authAPI.getProfile(profile.email)
        .then(async (response) => {
          if (response.data?.access_token && AUTH_STORAGE) {
            AUTH_STORAGE.setItem('app_jwt', response.data.access_token);
            if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('appJwtUpdated'));
            setAppJwtReady(true);
          }
          const backendUserData = response.data?.user;
          if (backendUserData) {
            // Charger l'avatar depuis les paramètres utilisateur
            let userAvatar = null;
            if (backendUserData.id) {
              try {
                const avatarResponse = await settingsAPI.get('user_avatar', null, backendUserData.id);
                if (avatarResponse.data?.param_value) {
                  userAvatar = avatarResponse.data.param_value;
                }
              } catch (error) {
                // Ignorer les erreurs (paramètre peut ne pas exister)
              }
            }
            
            // Mettre à jour le profil avec les données backend
            setProfile(prev => {
              if (!prev) return prev;
              const updatedProfile = {
                ...prev,
                user_id: backendUserData.id,
                organization_id: backendUserData.organization_id,
                role: backendUserData.role,
                is_owner: backendUserData.is_owner || false,
                is_superadmin: backendUserData.is_superadmin === true ? true : (backendUserData.is_superadmin === false ? false : false),
                // Utiliser l'avatar personnalisé s'il existe, sinon garder le picture de Google
                picture: userAvatar || prev.picture,
                allowed_tabs: backendUserData.allowed_tabs ?? prev.allowed_tabs,
                permissions: backendUserData.permissions ?? prev.permissions,
              };
              
              saveSession(updatedProfile);
              return updatedProfile;
            });
          } else {
            // Si pas de données backend, initialiser à false
            console.warn('⚠️ [Auth] Pas de données backend, initialisation à false');
            setProfile(prev => {
              if (!prev) {
                // Si prev est null, ne rien faire (on ne peut pas initialiser sans données de base)
                return prev;
              }
              // Utiliser optional chaining pour être sûr
              const updatedProfile = {
                ...prev,
                is_owner: prev?.is_owner !== undefined ? prev.is_owner : false,
                is_superadmin: prev?.is_superadmin !== undefined ? prev.is_superadmin : false,
              };
              saveSession(updatedProfile);
              return updatedProfile;
            });
          }
        })
        .catch((error) => {
          console.error('❌ [Auth] Erreur lors de la récupération du profil backend:', error);
          // En cas d'erreur, initialiser à false seulement si profile existe
          setProfile(prev => {
            if (!prev) {
              // Si prev est null, ne rien faire (on ne peut pas initialiser sans données de base)
              return prev;
            }
            // Utiliser optional chaining pour être sûr
            const updatedProfile = {
              ...prev,
              is_owner: prev?.is_owner !== undefined ? prev.is_owner : false,
              is_superadmin: prev?.is_superadmin !== undefined ? prev.is_superadmin : false,
            };
            saveSession(updatedProfile);
            return updatedProfile;
          });
        });
    }
  }, [profile?.email]); // Seulement quand l'email change

  // Rafraîchir le profil au retour sur l'onglet (prend en compte les mises à jour admin, ex. allowed_tabs)
  const lastProfileRefreshRef = React.useRef(0);
  const sessionTimeoutLoadedForUserIdRef = React.useRef(null);
  const PROFILE_REFRESH_THROTTLE_MS = 60000; // 1 minute
  useEffect(() => {
    if (!profile?.email) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastProfileRefreshRef.current < PROFILE_REFRESH_THROTTLE_MS) return;
        lastProfileRefreshRef.current = now;
        authAPI.getProfile(profile.email)
          .then((response) => {
            if (response.data?.access_token && AUTH_STORAGE) {
              AUTH_STORAGE.setItem('app_jwt', response.data.access_token);
              if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('appJwtUpdated'));
              setAppJwtReady(true);
            }
            const backendUserData = response.data?.user;
            if (backendUserData) {
              setProfile(prev => {
                if (!prev) return prev;
                const updatedProfile = {
                  ...prev,
                  allowed_tabs: backendUserData.allowed_tabs ?? prev.allowed_tabs,
                  role: backendUserData.role ?? prev.role,
                  is_owner: backendUserData.is_owner ?? prev.is_owner,
                  is_superadmin: backendUserData.is_superadmin ?? prev.is_superadmin,
                  permissions: backendUserData.permissions ?? prev.permissions,
                };
                saveSession(updatedProfile);
                return updatedProfile;
              });
            }
          })
          .catch(() => { /* ignorer les erreurs de rafraîchissement */ });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [profile?.email]);

  // Fonction pour mettre à jour immédiatement le profil avec picture: null (pour feedback instantané)
  const setAvatarToNull = React.useCallback(() => {
    setProfile(prev => {
      if (!prev) return prev;
      const updatedProfile = { ...prev, picture: null };
      saveSession(updatedProfile);
      return updatedProfile;
    });
  }, []);

  // Cache pour éviter les requêtes répétées après un 404 (avatar supprimé)
  const avatar404CacheRef = React.useRef({});
  const AVATAR_404_CACHE_DURATION = 60000; // 60 secondes - ne pas refaire de requête pendant 1 minute après un 404

  // Fonction pour effacer le cache 404 d'un utilisateur (utile après une sauvegarde réussie)
  const clearAvatar404Cache = React.useCallback((userId) => {
    if (userId) {
      const cacheKey = `user_${userId}`;
      delete avatar404CacheRef.current[cacheKey];
    }
  }, []);

  // Fonction pour recharger l'avatar depuis la base de données
  const refreshAvatar = React.useCallback(async (force = false) => {
    if (!profile || (!profile.user_id && !profile.id)) return;
    
    const userId = profile.user_id || profile.id;
    const cacheKey = `user_${userId}`;
    const cached404 = avatar404CacheRef.current[cacheKey];
    
    // Si on a eu un 404 récemment, ne pas refaire de requête pendant un certain temps
    // SAUF si force=true (après une sauvegarde réussie)
    if (!force && cached404 && (Date.now() - cached404.timestamp) < AVATAR_404_CACHE_DURATION) {
      // Ne pas faire de requête, mais s'assurer que picture est null
      if (profile.picture !== null) {
        setProfile(prev => {
          if (!prev) return prev;
          const updatedProfile = { ...prev, picture: null };
          saveSession(updatedProfile);
          return updatedProfile;
        });
      }
      return;
    }
    
    // Si force=true, effacer le cache avant de faire la requête
    if (force) {
      delete avatar404CacheRef.current[cacheKey];
    }
    
    try {
      const avatarResponse = await settingsAPI.get('user_avatar', null, userId);
      // Si on obtient une réponse, effacer le cache 404
      delete avatar404CacheRef.current[cacheKey];
      
      if (avatarResponse.data?.param_value) {
        const userAvatar = avatarResponse.data.param_value;
        // Toujours mettre à jour l'avatar depuis la base de données (source de vérité)
        setProfile(prev => {
          if (!prev) return prev;
          // Mettre à jour même si c'est identique pour s'assurer de la cohérence
          const updatedProfile = { ...prev, picture: userAvatar };
          saveSession(updatedProfile);
          return updatedProfile;
        });
      } else {
        // Si l'avatar a été supprimé (param_value vide ou null), toujours mettre picture à null
        // car cela signifie que l'utilisateur a explicitement supprimé son avatar personnalisé
        setProfile(prev => {
          if (!prev) return prev;
          // Mettre picture à null pour forcer l'affichage des initiales
          const updatedProfile = { ...prev, picture: null };
          saveSession(updatedProfile);
          return updatedProfile;
        });
      }
    } catch (error) {
      // Si c'est une erreur 404, cela signifie que le paramètre n'existe pas (avatar supprimé)
      // Dans ce cas, mettre picture à null et mettre en cache pour éviter les requêtes répétées
      if (error.response?.status === 404) {
        // Mettre en cache le 404 pour éviter les requêtes répétées (sauf si force=true)
        if (!force) {
          avatar404CacheRef.current[cacheKey] = { timestamp: Date.now() };
        }
        
        setProfile(prev => {
          if (!prev) return prev;
          const updatedProfile = { ...prev, picture: null };
          saveSession(updatedProfile);
          return updatedProfile;
        });
      }
    }
  }, [profile?.user_id, profile?.id]);

  // PHASE 1: Polling optimisé - chargement de l'avatar seulement au montage
  // Le polling automatique toutes les 30 secondes a été supprimé pour réduire les appels API redondants
  // L'avatar sera rechargé manuellement si nécessaire (ex: après modification dans Parametres)
  useEffect(() => {
    if (!profile || (!profile.user_id && !profile.id)) return;
    
    // Charger l'avatar seulement une fois au montage
    refreshAvatar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.user_id, profile?.id]); // Ne pas inclure refreshAvatar pour éviter les re-renders

  // Charger la valeur du timeout depuis la base de données au démarrage (une seule fois par user pour éviter appels doublons)
  useEffect(() => {
    if (!profile) {
      sessionTimeoutLoadedForUserIdRef.current = null;
      return;
    }
    const userId = profile?.id || profile?.user_id;
    if (!userId) return;
    if (sessionTimeoutLoadedForUserIdRef.current === userId) return;
    sessionTimeoutLoadedForUserIdRef.current = userId;

    const loadTimeoutFromDB = async () => {
      try {
        const response = await settingsAPI.get('session_timeout_minutes', null, userId);
        if (response.data?.param_value) {
          const minutes = parseInt(response.data.param_value, 10);
          if (!isNaN(minutes) && minutes > 0) {
            setSessionTimeoutMinutes(minutes);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⏱️ [Session Timeout] Impossible de charger depuis la DB (utilise la valeur du localStorage):', error);
        }
      }
    };

    loadTimeoutFromDB();
  }, [profile]);

  // Persister la durée d'inactivité quand elle change
  useEffect(() => {
    try {
      if (sessionTimeoutMinutes && sessionTimeoutMinutes > 0) {
        localStorage.setItem('sessionInactivityMinutes', String(sessionTimeoutMinutes));
      }
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [sessionTimeoutMinutes]);

  const login = useGoogleLogin({
    scope: 'openid email profile',
    onSuccess: (tokenResponse) => {
      setAuthError(null);
      // Récupérer les informations du profil
      fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      })
        .then((res) => res.json())
        .then(async (data) => {
          setLoggingIn(true);
          try {
          // Vérifier que l'email est autorisé
          if (!isEmailAuthorized(data.email)) {
            setAuthError(`Accès refusé pour ${data.email}. Contactez l'administrateur pour obtenir l'accès.`);
            setUser(null);
            setProfile(null);
            setAppJwtReady(false);
            deleteSession();
            setTimeout(() => setLoggingIn(false), 500);
            return;
          }

          // Récupérer les informations utilisateur depuis le backend pour obtenir is_owner, is_superadmin, role, etc.
          try {
            const profileResponse = await authAPI.getProfile(data.email);
            const backendUserData = profileResponse.data?.user;
            // Enregistrer le nouveau JWT tout de suite pour que les appels suivants (avatar) utilisent le bon token (évite 401 sur cette machine)
            if (AUTH_STORAGE && profileResponse.data?.access_token) {
              AUTH_STORAGE.setItem('app_jwt', profileResponse.data.access_token);
              setAppJwtReady(true);
            }
            if (backendUserData) {
              // Charger l'avatar depuis les paramètres utilisateur (utilise déjà le nouveau app_jwt)
              let userAvatar = null;
              if (backendUserData.id) {
                try {
                  const avatarResponse = await settingsAPI.get('user_avatar', null, backendUserData.id);
                  if (avatarResponse.data?.param_value) {
                    userAvatar = avatarResponse.data.param_value;
                  }
                } catch (error) {
                  // Ignorer les erreurs (paramètre peut ne pas exister)
                }
              }
              
              // Fusionner les données Google avec les données backend
              const sessionData = {
                email: data.email,
                name: data.name || `${backendUserData.first_name || ''} ${backendUserData.last_name || ''}`.trim() || data.email,
                picture: userAvatar || data.picture, // Utiliser l'avatar personnalisé s'il existe, sinon celui de Google
                expiresAt: new Date(Date.now() + SESSION_DURATION_HOURS * 60 * 60 * 1000).toISOString(),
                user_id: backendUserData.id,
                organization_id: backendUserData.organization_id,
                role: backendUserData.role,
                is_owner: backendUserData.is_owner || false,
                is_superadmin: backendUserData.is_superadmin || false,
                allowed_tabs: backendUserData.allowed_tabs ?? null,
                permissions: backendUserData.permissions ?? null,
              };
              
              // Sauvegarder la session avec les données complètes
              saveSession(sessionData);
              setUser(tokenResponse);
              setProfile(sessionData);
              if (AUTH_STORAGE) {
                AUTH_STORAGE.setItem('googleUser', JSON.stringify(tokenResponse));
              }
            } else {
              // Fallback si le backend ne retourne pas de données
              console.warn('⚠️ [Auth] Pas de données backend, utilisation des données Google uniquement');
              const sessionData = saveSession(data);
              setUser(tokenResponse);
              setProfile(sessionData);
              if (AUTH_STORAGE) AUTH_STORAGE.setItem('googleUser', JSON.stringify(tokenResponse));
            }
          } catch (profileError) {
            console.error('⚠️ [Auth] Erreur lors de la récupération du profil backend:', profileError);
            const sessionData = saveSession(data);
            setUser(tokenResponse);
            setProfile(sessionData);
            if (AUTH_STORAGE) AUTH_STORAGE.setItem('googleUser', JSON.stringify(tokenResponse));
          }
          } finally {
            setTimeout(() => setLoggingIn(false), 5000);
          }
        })
        .catch((err) => {
          console.error('Error fetching user profile:', err);
          setAuthError('Erreur lors de la récupération du profil utilisateur');
          setTimeout(() => setLoggingIn(false), 500);
        });
    },
    onError: (error) => {
      console.error('Login failed:', error);
      setAuthError('Erreur lors de la connexion Google');
    },
  });

  const loginWithEmail = async (email, password) => {
    setLoggingIn(true);
    try {
      setAuthError(null);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 
                        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');
      const response = await authAPI.login(email, password);
      const { user: userData } = response.data;
      // Enregistrer le nouveau JWT tout de suite pour que les appels suivants (avatar) utilisent le bon token (évite 401 sur cette machine)
      if (AUTH_STORAGE && response.data?.access_token) {
        AUTH_STORAGE.setItem('app_jwt', response.data.access_token);
      }
      // Pour les connexions email/mot de passe, si le backend a accepté la connexion,
      // cela signifie que l'utilisateur existe et est actif dans la base de données.
      // On n'applique plus la whitelist hardcodée pour les connexions email,
      // car les utilisateurs sont gérés via l'interface d'administration.
      // La whitelist reste active uniquement pour les connexions Google.

      // Charger l'avatar depuis les paramètres utilisateur (utilise déjà le nouveau app_jwt)
      let userAvatar = null;
      if (userData.id) {
        try {
          const avatarResponse = await settingsAPI.get('user_avatar', null, userData.id);
          if (avatarResponse.data?.param_value) {
            userAvatar = avatarResponse.data.param_value;
          }
        } catch (error) {
          // Ignorer les erreurs (paramètre peut ne pas exister)
        }
      }
      
      // Créer un objet de session similaire à celui de Google
      const sessionData = {
        email: userData.email,
        name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email,
        picture: userAvatar, // Utiliser l'avatar personnalisé s'il existe
        expiresAt: new Date(Date.now() + SESSION_DURATION_HOURS * 60 * 60 * 1000).toISOString(),
        user_id: userData.id,
        organization_id: userData.organization_id,
        role: userData.role,
        is_owner: userData.is_owner || false,
        is_superadmin: userData.is_superadmin || false,
        allowed_tabs: userData.allowed_tabs ?? null,
        permissions: userData.permissions ?? null,
      };
      
      // Sauvegarder la session (app_jwt déjà enregistré plus haut)
      saveSession(sessionData);
      setUser({ id: userData.id, email: userData.email });
      setProfile(sessionData);
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Erreur lors de la connexion';
      
      // Utiliser le message d'erreur amélioré de l'intercepteur Axios si disponible
      if (error.message && error.message !== 'Network Error' && error.message !== 'Request failed with status code') {
        errorMessage = error.message;
      } else if (error.response) {
        // Erreur HTTP avec réponse
        const status = error.response.status;
        const detail = error.response.data?.detail || error.response.data?.message;
        
        if (status === 401) {
          errorMessage = detail || 'Email ou mot de passe incorrect';
        } else if (status === 403) {
          errorMessage = detail || 'Compte utilisateur désactivé';
        } else if (status === 503) {
          // Saturation PostgreSQL ou DB indisponible — pas une erreur d’identifiants
          errorMessage =
            (typeof detail === 'string' ? detail : null) ||
            error.response?.data?.message ||
            'Service temporairement indisponible. Réessayez dans quelques instants.';
        } else if (status === 404) {
          errorMessage = 'Endpoint de connexion non trouvé. Vérifiez que le backend est démarré.';
        } else {
          errorMessage = detail || `Erreur ${status} lors de la connexion`;
        }
      } else if (error.request) {
        // Requête envoyée mais pas de réponse - utiliser le message de l'intercepteur si disponible
        if (error.message && error.message.includes('serveur')) {
          errorMessage = error.message;
        } else {
          errorMessage =
            process.env.NODE_ENV === 'production'
              ? 'Le serveur ne répond pas dans les temps. Réessayez dans un instant ou contactez l’administrateur.'
              : 'Le serveur ne répond pas. Vérifiez que le backend est démarré sur http://localhost:8000';
        }
      } else {
        // Erreur de configuration
        errorMessage = error.message || 'Erreur lors de la connexion';
      }
      
      setAuthError(errorMessage);
      setUser(null);
      setProfile(null);
      setAppJwtReady(false);
      deleteSession();
    } finally {
      // Réactiver les déconnexions globales après un délai (évite 401 sur /auth/validate juste après login)
      setTimeout(() => setLoggingIn(false), 5000);
    }
  };

  const logout = () => {
    googleLogout();
    setUser(null);
    setProfile(null);
    setAppJwtReady(false);
    deleteSession();
    setAuthError(null);
  };

  // Déconnexion quand la session est invalidée (ex. connexion depuis un autre appareil)
  useEffect(() => {
    const handleLogoutRequired = (e) => {
      const message = e.detail?.message || 'Connexion depuis un autre appareil. Veuillez vous reconnecter.';
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('auth_logout_reason', message);
        }
      } catch (_) { /* ignorer */ }
      logout();
    };
    window.addEventListener('auth:logout-required', handleLogoutRequired);
    return () => window.removeEventListener('auth:logout-required', handleLogoutRequired);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- logout stable

  // ❌ DÉSACTIVÉ TEMPORAIREMENT : Le polling cause des 401 intempestifs et déconnexions non voulues.
  /*
  const validateIntervalRef = useRef(null);
  const SESSION_VALIDATE_INTERVAL_MS = 45000; // 45 s
  const SESSION_VALIDATE_INITIAL_DELAY_MS = 10000; // 10 s avant le premier check
  useEffect(() => {
    if (!profile) return;
    let pollCount = 0;
    const validateSession = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      pollCount += 1;
      authAPI.validate().catch(() => {});
    };
    const initialDelayId = setTimeout(() => {
      validateSession();
      validateIntervalRef.current = setInterval(validateSession, SESSION_VALIDATE_INTERVAL_MS);
    }, SESSION_VALIDATE_INITIAL_DELAY_MS);
    return () => {
      clearTimeout(initialDelayId);
      if (validateIntervalRef.current) {
        clearInterval(validateIntervalRef.current);
        validateIntervalRef.current = null;
      }
    };
  }, [profile]);
  */

  // Vérifier l'expiration de la session périodiquement
  useEffect(() => {
    const checkSessionExpiry = () => {
      const session = loadSession();
      if (!session && profile) {
        // Session expirée
        setProfile(null);
        setUser(null);
        setAppJwtReady(false);
        deleteSession();
      }
    };

    const interval = setInterval(checkSessionExpiry, 60000); // Vérifier toutes les minutes
    return () => clearInterval(interval);
  }, [profile]);

  // Déconnexion automatique après inactivité (basée sur sessionTimeoutMinutes)
  // Note: le timer est mis en PAUSE quand l'onglet est en arrière-plan (document.hidden)
  // pour éviter qu'une connexion avec un autre compte dans un autre onglet ne déconnecte le premier.
  // Throttle: on ne réinitialise le timer qu'au plus toutes les 2 s pour éviter que scroll/touch
  // ne réinitialisent en continu et empêchent toute déconnexion.
  useEffect(() => {
    if (!profile) return;

    let timeoutId;
    let lastLogTime = 0;
    let lastResetTime = 0;
    const LOG_INTERVAL_MS = 5000; // Logger au maximum une fois toutes les 5 secondes
    const RESET_THROTTLE_MS = 10000; // Réinitialiser le timer au plus toutes les 10 s (évite que scroll/touch mobile reset en continu)

    const resetInactivityTimer = () => {
      const now = Date.now();
      // Throttle: ignorer les événements trop rapprochés (scroll/touch sur mobile)
      if (timeoutId != null && now - lastResetTime < RESET_THROTTLE_MS) {
        return;
      }
      lastResetTime = now;

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      // Ne pas lancer le timer si l'onglet est en arrière-plan (autre onglet actif)
      if (document.hidden) return;

      // Si la valeur est invalide ou <= 0, ne pas activer le timer
      if (!sessionTimeoutMinutes || sessionTimeoutMinutes <= 0) {
        if (process.env.NODE_ENV === 'development' && now - lastLogTime > LOG_INTERVAL_MS) {
          console.warn('⏱️ [Session Timeout] Valeur invalide:', sessionTimeoutMinutes, '- utilisation de la valeur par défaut 30 minutes');
          lastLogTime = now;
        }
        const defaultMinutes = DEFAULT_INACTIVITY_MINUTES;
        timeoutId = setTimeout(() => {
          logout();
        }, defaultMinutes * 60 * 1000);
        return;
      }

      const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
      if (process.env.NODE_ENV === 'development' && now - lastLogTime > LOG_INTERVAL_MS) {
        lastLogTime = now;
      }

      timeoutId = setTimeout(() => {
        logout();
      }, timeoutMs);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, resetInactivityTimer));

    // Au retour sur l'onglet: réactiver le timer (remplace le compteur qui tournait en arrière-plan)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetInactivityTimer();
      } else {
        // Onglet en arrière-plan: mettre en pause le timer
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Démarrer le timer dès que l'utilisateur est connecté (si l'onglet est visible)
    resetInactivityTimer();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      events.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [profile, sessionTimeoutMinutes]);

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        profile, 
        login, 
        loginWithEmail,
        logout, 
        isAuthenticated: !!profile,
        sessionCheckDone, // false tant que la session n'a pas été validée au chargement (évite toast "Session terminée" avec token expiré)
        appJwtReady, // true une fois qu'un JWT a été stocké ou validé → Oliver attend avant d'appeler /ai/providers et /ai/chat
        authError,
        clearError: () => setAuthError(null),
        sessionTimeoutMinutes,
        setSessionTimeoutMinutes,
        refreshAvatar, // Exposer la fonction pour forcer le rechargement de l'avatar
        setAvatarToNull, // Exposer la fonction pour mettre picture à null immédiatement
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

